import type { IModelCreatedNotificationData } from "@frontend-shared/types";
import { Sparkles } from "lucide-react";
import { memo, useCallback } from "react";
import { Trans, useTranslation } from "react-i18next";

import { NotificationCardShell } from "../NotificationCardShell";

interface IModelCreatedCardProps {
  uuid: string;
  data: IModelCreatedNotificationData;
  createdAt: string;
  isRead: boolean;
  onViewModels: (projectUuid: string) => void;
  onStartChat: (modelUuid: string, projectUuid: string) => void;
}

const ModelCreatedCardComponent = ({
  uuid,
  data,
  createdAt,
  isRead,
  onViewModels,
  onStartChat,
}: IModelCreatedCardProps): React.JSX.Element => {
  const { t } = useTranslation("notifications");

  const handleViewModels = useCallback(
    () => onViewModels(data.projectUuid),
    [onViewModels, data.projectUuid],
  );

  const handleStartChat = useCallback(
    () => onStartChat(data.modelUuid, data.projectUuid),
    [onStartChat, data.modelUuid, data.projectUuid],
  );

  return (
    <NotificationCardShell
      uuid={uuid}
      variantTestId="model_created"
      createdAt={createdAt}
      isRead={isRead}
      icon={<Sparkles className="h-5 w-5 text-violet-500" />}
      iconBgClassName="bg-violet-500/10"
      ctaLabel={t("view_models")}
      onCtaClick={handleViewModels}
      secondaryCtaLabel={t("start_chat")}
      onSecondaryCtaClick={handleStartChat}
      body={
        <Trans
          ns="notifications"
          i18nKey="type.model_created.body"
          values={{
            creatorName: data.creatorName,
            modelTitle: data.modelTitle,
            projectTitle: data.projectTitle,
          }}
          components={{ b: <strong /> }}
        />
      }
    />
  );
};

export const ModelCreatedCard = memo(ModelCreatedCardComponent);
